import type { Chart } from '@usacsmideo/types';
import QRCode from 'react-qr-code';
import { formatDateForDisplay } from '../../utilities/formatDateForDisplay';
import { formatPhoneDisplay } from '../../utils/phone';

type CardFrontProps = {
  chart: Chart;
  patientId: string;
  mode: 'preview' | 'final';
};

export function CardFront({ chart, patientId, mode }: CardFrontProps) {
  const qrValue = chart?.STATUS?.emergencyAccessTokenId
    ? `${import.meta.env.VITE_EMERGENCY_ACCESS_URL}/e/${chart.STATUS.emergencyAccessTokenId}`
    : `${import.meta.env.VITE_VIDEO_STORAGE_URL}/${patientId}/finalVideo.mp4`;

  return (
    <div
      id="card-front"
      className="i bg-true-white @container my-4 aspect-[1.586] w-[300px] flex-col rounded-xl outline-solid sm:w-[540px]"
    >
      <div id="card-front-header" className="bg-mideo-darkred">
        <p className="p-2 text-xs text-white sm:text-xl">
          MIDEO<sup>{String.fromCharCode(0x000a9)}</sup> - My Informed Decision on VidEO
          {String.fromCharCode(0x02122)}
        </p>
      </div>
      <div id="card-front-body-container" className="flex flex-row">
        <div id="card-front-body-left" className="flex flex-col pt-2 pl-2 text-[0.45rem] sm:text-base">
          <div id="patient-name" className="text-sm font-bold sm:text-2xl">
            <p>
              {`${chart.PATIENT_INFO_1.firstName} `}
              {chart.PATIENT_INFO_1.middleInitial ? `${chart.PATIENT_INFO_1.middleInitial}. ` : ' '}
              {`${chart.PATIENT_INFO_1.lastName}`}
            </p>
          </div>
          <div id="code-status" className="flex flex-row">
            <div id="status-title" className="w-3/12">
              <p>Code Status: </p>
            </div>
            <div id="status-text" className="w-9/12">
              <p>
                {chart?.SELECTED_TREATMENT_OPTION?.description
                  ? chart.SELECTED_TREATMENT_OPTION.description
                  : 'Full Code - All Treatment & No Limitations in Care'}
              </p>
            </div>
          </div>
          <div id="organ-donor" className="flex flex-row">
            <div id="organ-donor-title">
              <p>Organ Donor:</p>
            </div>
            <div id="organ-donor-text" className="ml-2">
              <p>{chart.LIVING_WILL.organDonor}</p>
            </div>
          </div>
          <div id="living-will" className="t flex flex-row">
            <p>Living Will:</p>
            <p className="ml-2">{chart.OTHER_DOCUMENTS.livingWillExists === true ? 'Yes' : 'No'}</p>
          </div>
          <div id="health-care-agent-name" className="flex flex-row">
            <div id="health-care-agent-title">
              <p>Health Care Agent:</p>
            </div>
            <div id="health-care-agent-text" className="ml-2">
              <p>{chart.HEALTHCARE_AGENT_INFO.agent}</p>
            </div>
          </div>
          <div id="health-care-agent-phone" className="flex flex-row">
            <div id="health-care-agent-phone-title">
              <p>Phone:</p>
            </div>
            <div id="health-care-agent-phone-text" className="ml-2">
              <p>
                {formatPhoneDisplay(chart.HEALTHCARE_AGENT_INFO.primaryPhone || chart.HEALTHCARE_AGENT_INFO.cellPhone)}
              </p>
            </div>
          </div>
          <div id="medical-order" className="t flex flex-row">
            <p>{chart.PATIENT_INFO_2.state.toLowerCase().includes('md') ? `MOLST:` : `POLST:`}</p>
            <p className="ml-2">{chart.OTHER_DOCUMENTS.POLSTExists === true ? 'Yes' : 'No'}</p>
          </div>
          <div id="card-front-left-footer-" className="mt-4 flex flex-row items-center sm:mt-6">
            <div id="front-logo">
              <img
                src="https://storage.googleapis.com/web-console-mideoid-public/logos/defaultByLogo.png"
                alt="MIDEO Health Logo"
                className="h-8 sm:h-14"
              />
            </div>
            <div id="expiry-info" className="mx-2 text-[0.45rem] sm:text-base">
              {mode === 'preview' ? (
                <>
                  <p>ISSUED: YYYY-MM-DD</p>
                  <p>EXPIRES: YYYY-MM-DD</p>
                </>
              ) : (
                <>
                  <p>ISSUED: {chart.STATUS?.cardIssueDate || 'N/A'}</p>
                  <p>EXPIRES: {chart.STATUS?.cardExpiryDate || 'N/A'}</p>
                </>
              )}
            </div>
          </div>
        </div>
        <div id="card-front-body-right" className="my-2 flex w-4/12 flex-col">
          <div id="patient-photo" className="flex flex-col items-center">
            <img src={chart.PHOTO_FOR_CARD.portraitURI} alt="Patient" className="mr-2 h-14 sm:mr-4 sm:h-22" />
          </div>
          <div id="DOB-info" className="mr-2 flex flex-col items-center text-[0.4rem] sm:text-sm">
            <p>DOB:</p>
            <p>{formatDateForDisplay(chart.PATIENT_INFO_1.DOB)}</p>
          </div>
          <div id="front-qr-code" className="mr-4 sm:mt-8">
            {mode === 'preview' ? (
              <img src="/sampleQR.png" alt="Sample QR Code" className="h-14 sm:h-28" />
            ) : (
              <QRCode
                size={256}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                value={qrValue}
                viewBox="0 0 256 256"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
